import * as React from 'react';
import * as moment from 'moment';
import { connect } from 'react-redux';
import { find, keys, groupBy, merge } from 'lodash';
import { Card, Notification } from '../';
import { getBookingRange } from '../../redux/actions/bookings';
import StatisticsTable from './StatisticsTable';
import StatisticsControls from './StatisticsControls';

import VehicleType from './IVehicleType';
import Statistic from './IStatistic';

interface Props {
  vehicleTypes: Array<VehicleType>;
  dispatch: Function;
}

interface State {
  startDate: Date;
  endDate: Date;
  statistics: Array<Statistic>;
  isFetching: boolean;
  errorMessage: string | boolean;
}

class Statistics extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      startDate: moment()
        .startOf('week')
        .add(1, 'day')
        .toDate(),
      endDate: moment()
        .endOf('week')
        .add(1, 'day')
        .toDate(),
      statistics: [],
      isFetching: false,
      errorMessage: false,
    };
    this.getStatistics = this.getStatistics.bind(this);
  }

  /**
   * Get the vehicle types once and get the statistics for the
   * current date
   */
  async componentDidMount() {
    this.setState({ isFetching: true });
    await this.getStatistics(this.state.startDate, this.state.endDate);
  }

  /**
   * Get the statistics for the date range provided
   * @param {Date} startDate
   * @param {Date} endDate
   */
  async getStatistics(startDate: Date, endDate: Date) {
    // Ensure timestamps are correct, as MySQL queries take that into consideration
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).endOf('day');

    // Validate date range
    if (start.isAfter(end)) {
      this.setState({
        errorMessage: 'The start date cannot be after the end date',
      });
      return;
    }

    // Prepare to fetch
    this.setState({ isFetching: true, errorMessage: false });

    // Get all bookings, and group them by vehicle type
    const response = await this.props.dispatch(
      getBookingRange(
        start.format('YYYY-MM-DD HH:mm:ss'),
        end.format('YYYY-MM-DD HH:mm:ss'),
        false,
        true,
      ),
    );

    // Exclude all cancellations
    const filteredResponse = response.data.filter(
      (res) => res.status !== 'CANCELLED',
    );

    const bookings = merge(groupBy(filteredResponse, 'vehicle_type_id'));

    // Calculates the duration of a booking, but clamps it to the specified range
    const bookingDuration = (booking) => {
      const clampedStart = moment.max(moment(booking.start), start);
      const clampedEnd = moment.min(moment(booking.end), end);
      const duration = clampedEnd.diff(clampedStart, 'days') + 1;
      console.log(booking.invoiceId, duration);
      return duration;
    };

    // Convert the bookings as statistics
    const statistics: Array<Statistic> = [];
    keys(bookings).forEach((vehicleTypeId) =>
      statistics.push({
        vehicleType: find(this.props.vehicleTypes, {
          id: Number(vehicleTypeId),
        }),
        bookings: bookings[vehicleTypeId].length,
        days: bookings[vehicleTypeId]
          .map(bookingDuration)
          .reduce((a, b) => a + b),
      }),
    );

    // Finish
    this.setState({ statistics, isFetching: false });
  }

  render() {
    return (
      <Card>
        <div className="columns">
          <div className="column">
            <StatisticsControls
              isFetching={this.state.isFetching}
              startDate={this.state.startDate}
              endDate={this.state.endDate}
              handleChange={({ startDate, endDate }) => {
                if (!this.state.isFetching) {
                  this.setState(
                    {
                      startDate: startDate || this.state.startDate,
                      endDate: endDate || this.state.endDate,
                    },
                    () =>
                      this.getStatistics(
                        this.state.startDate,
                        this.state.endDate,
                      ),
                  );
                }
              }}
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <StatisticsTable
              isFetching={this.state.isFetching}
              statistics={this.state.statistics}
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            {this.state.errorMessage && (
              <Notification type="danger">
                {this.state.errorMessage}
              </Notification>
            )}
          </div>
        </div>
      </Card>
    );
  }
}

const mapStateToProps = ({ vehicleTypes }) => ({
  vehicleTypes: vehicleTypes.vehicleTypes,
});

export default connect(mapStateToProps)(Statistics);
